/**
 * The canonical ENGLISH values of the tenant-customizable terminology variables.
 *
 * These are i18next's `interpolation.defaultVariables` at init (see `./config`),
 * so `{{Contacts}}` renders as "Contacts" before the API answers. Once the
 * tenant's terms load, `applyTenantI18nOverrides` REPLACES them with that
 * tenant's terms for the active locale — which is why anything that needs the
 * English word specifically (navigation search's English aliases) must pass
 * this map explicitly rather than trust the global defaults.
 *
 * It lives in its own leaf module, not in `./config`, so a consumer can read the
 * canonical English vocabulary without pulling in i18next initialisation and
 * every eagerly-bundled English locale.
 *
 * {{Product}} = title case, {{product}} = lowercase. Same pattern for all 11
 * terms; `vendor_order` decouples the variable name ({{VendorOrder}}) from its
 * display default ("Vendor quote/order/pro-forma/invoice") via the config
 * "variable" field. Canonical term list: `shared/config/i18n_terms.config.json`.
 *
 * Each term also carries a fifth variable, `{{<term>Gender}}` — the grammatical
 * gender the `agree` formatter selects on (see ./agreeFormatter). English has no
 * grammatical gender, so every entry here is `'n'`; the Spanish and Hebrew values
 * arrive with the tenant's terms from the API.
 */
export const TERMINOLOGY_DEFAULT_VARIABLES: Record<string, string> = {
  Product: 'Product', Products: 'Products', product: 'product', products: 'products',
  Deal: 'Deal', Deals: 'Deals', deal: 'deal', deals: 'deals',
  Contact: 'Contact', Contacts: 'Contacts', contact: 'contact', contacts: 'contacts',
  Ticket: 'Ticket', Tickets: 'Tickets', ticket: 'ticket', tickets: 'tickets',
  Agent: 'Agent', Agents: 'Agents', agent: 'agent', agents: 'agents',
  Company: 'Company', Companies: 'Companies', company: 'company', companies: 'companies',
  Organization: 'Organization', Organizations: 'Organizations', organization: 'organization', organizations: 'organizations',
  Task: 'Task', Tasks: 'Tasks', task: 'task', tasks: 'tasks',
  Employee: 'Employee', Employees: 'Employees', employee: 'employee', employees: 'employees',
  Contract: 'Contract', Contracts: 'Contracts', contract: 'contract', contracts: 'contracts',
  VendorOrder: 'Vendor quote/order/pro-forma/invoice', VendorOrders: 'Vendor quotes/orders/pro-formas/invoices',
  vendorOrder: 'vendor quote/order/pro-forma/invoice', vendorOrders: 'vendor quotes/orders/pro-formas/invoices',

  // Grammatical gender (English: none). Overwritten per locale by the terms API.
  productGender: 'n',
  dealGender: 'n',
  contactGender: 'n',
  ticketGender: 'n',
  agentGender: 'n',
  companyGender: 'n',
  organizationGender: 'n',
  taskGender: 'n',
  employeeGender: 'n',
  contractGender: 'n',
  vendorOrderGender: 'n',
};
